import React, { useEffect, useState } from 'react';
import { Briefcase, Plus, RefreshCw, UserPlus, X, Trash2 } from 'lucide-react';
import api from '../services/api';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import ConfirmModal from '../components/ConfirmModal';
import './Users.css';
import './TasksRewards.css';

const Projects = () => {
  useMarkAsReadOnMount(['project', 'task', 'reward']);

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedContributor, setSelectedContributor] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAddContributor, setShowAddContributor] = useState(false);
  const [addContributorId, setAddContributorId] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const [form, setForm] = useState({
    name: '',
    departmentId: '',
    description: '',
    startDate: '',
    endDate: '',
    managerIds: [],
    totalRewardPool: '',
    contributorIds: [],
  });

  const [quotaForm, setQuotaForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    quota: '',
  });

  const [assignTaskForm, setAssignTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimatedHours: '',
    dueDate: '',
  });
  const [projectSettingsForm, setProjectSettingsForm] = useState({
    name: '',
    departmentId: '',
    managerIds: [],
    endDate: '',
    totalRewardPool: '',
    status: 'active',
  });
  const [taskEdits, setTaskEdits] = useState({});

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const getProjectStatusStyles = (status) => {
    const normalized = String(status || 'active').toLowerCase();
    if (normalized === 'completed') {
      return {
        background: 'rgba(59, 130, 246, 0.14)',
        color: '#93c5fd',
        border: '1px solid rgba(59, 130, 246, 0.35)',
      };
    }
    if (normalized === 'cancelled') {
      return {
        background: 'rgba(239, 68, 68, 0.14)',
        color: '#fca5a5',
        border: '1px solid rgba(239, 68, 68, 0.35)',
      };
    }
    return {
      background: 'rgba(34, 197, 94, 0.14)',
      color: '#86efac',
      border: '1px solid rgba(34, 197, 94, 0.35)',
    };
  };

  const getTaskPriorityWeight = (priority) => {
    const normalized = String(priority || 'low').toLowerCase();
    if (normalized === 'medium') return 1.5;
    if (normalized === 'high' || normalized === 'urgent') return 2;
    return 1;
  };

  const getYearMonthKey = (value) => {
    if (!value) return '';
    return String(value).substring(0, 7);
  };

  const getQuotaWeightedCompletion = (quotaRow) => {
    const monthKey = `${quotaRow.year}-${String(quotaRow.month).padStart(2, '0')}`;
    const weightedDone = (selectedProject?.tasks || [])
      .filter((t) => {
        if (Number(t.userId) !== Number(quotaRow.userId)) return false;
        if (String(t.status || '').toLowerCase() !== 'completed') return false;
        return getYearMonthKey(t.completedDate) === monthKey;
      })
      .reduce((sum, t) => sum + getTaskPriorityWeight(t.priority), 0);

    return Number(weightedDone.toFixed(2));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, usersRes, deptsRes] = await Promise.all([
        api.get('/projects').catch(() => []),
        api.get('/users').catch(() => []),
        api.get('/departments').catch(() => []),
      ]);
      setProjects(normalizeArray(projectsRes));
      setUsers(normalizeArray(usersRes));
      setDepartments(normalizeArray(deptsRes));
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load projects.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!form.departmentId) {
      setMessage({ type: 'error', text: 'Please select a department for the project.' });
      return;
    }
    try {
      await api.post('/projects', {
        name: form.name,
        departmentId: Number(form.departmentId),
        description: form.description || undefined,
        startDate: form.startDate,
        endDate: form.endDate,
        managerIds: form.managerIds.map(Number),
        totalRewardPool: form.totalRewardPool !== '' ? Number(form.totalRewardPool) : 0,
        contributorIds: form.contributorIds.map(Number),
      });
      setMessage({ type: 'success', text: 'Project created successfully.' });
      setForm({ name: '', departmentId: '', description: '', startDate: '', endDate: '', managerIds: [], totalRewardPool: '', contributorIds: [] });
      setShowForm(false);
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to create project.' });
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      await api.delete(`/projects/${projectToDelete.id}`);
      setMessage({ type: 'success', text: `Project "${projectToDelete.name}" deleted successfully.` });
      if (selectedProject?.id === projectToDelete.id) {
        setSelectedProject(null);
      }
      setShowDeleteModal(false);
      setProjectToDelete(null);
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete project.' });
    }
  };

  const handleSetQuota = async (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedContributor) return;
    try {
      await api.post(`/projects/${selectedProject.id}/quota`, {
        userId: Number(selectedContributor.id),
        month: Number(quotaForm.month),
        year: Number(quotaForm.year),
        quota: Number(quotaForm.quota),
      });
      setMessage({ type: 'success', text: 'Monthly quota set.' });
      setQuotaForm({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), quota: '' });
      await refreshSelectedProject();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to set quota.' });
    }
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!selectedContributor?.id || !assignTaskForm.title) {
      setMessage({ type: 'error', text: 'Task title is required.' });
      return;
    }

    const basePayload = {
      title: assignTaskForm.title,
      description: assignTaskForm.description || undefined,
      userId: Number(selectedContributor.id),
      priority: assignTaskForm.priority,
      estimatedHours: assignTaskForm.estimatedHours ? Number(assignTaskForm.estimatedHours) : undefined,
      dueDate: assignTaskForm.dueDate || undefined,
    };

    try {
      await api.post('/tasks', {
        ...basePayload,
        projectId: selectedProject?.id ?? undefined,
      });

      setAssignTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        estimatedHours: '',
        dueDate: '',
      });
      await refreshSelectedProject();
      setMessage({ type: 'success', text: 'Task assigned successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to assign task.' });
    }
  };

  const handleSelectProject = async (projectId) => {
    setShowAddContributor(false);
    setAddContributorId('');
    setSelectedContributor(null);
    try {
      await api.post(`/projects/${projectId}/calculate-rewards`);
      const project = await api.get(`/projects/${projectId}/with-rewards`);
      setSelectedProject(project);
      const mIds = (project.managers && project.managers.length > 0)
        ? project.managers.map((m) => m.id)
        : (project.managerId ? [project.managerId] : []);
      setProjectSettingsForm({
        name: project.name || '',
        departmentId: project.departmentId || project.department?.id || '',
        managerIds: mIds,
        endDate: project.endDate || '',
        totalRewardPool: String(project.totalRewardPool ?? ''),
        status: project.status || 'active',
      });
    } catch {
      const fallbackProject = projects.find((p) => p.id === projectId);
      setSelectedProject(fallbackProject);
      if (fallbackProject) {
        const mIds = (fallbackProject.managers && fallbackProject.managers.length > 0)
          ? fallbackProject.managers.map((m) => m.id)
          : (fallbackProject.managerId ? [fallbackProject.managerId] : []);
        setProjectSettingsForm({
          name: fallbackProject.name || '',
          departmentId: fallbackProject.departmentId || fallbackProject.department?.id || '',
          managerIds: mIds,
          endDate: fallbackProject.endDate || '',
          totalRewardPool: String(fallbackProject.totalRewardPool ?? ''),
          status: fallbackProject.status || 'active',
        });
      }
    }
  };

  const refreshSelectedProject = async () => {
    if (!selectedProject) return;
    try {
      const project = await api.get(`/projects/${selectedProject.id}/with-rewards`);
      setSelectedProject(project);
      const mIds = (project.managers && project.managers.length > 0)
        ? project.managers.map((m) => m.id)
        : (project.managerId ? [project.managerId] : []);
      setProjectSettingsForm({
        name: project.name || '',
        departmentId: project.departmentId || project.department?.id || '',
        managerIds: mIds,
        endDate: project.endDate || '',
        totalRewardPool: String(project.totalRewardPool ?? ''),
        status: project.status || 'active',
      });
    } catch {}
  };

  const handleUpdateProjectSettings = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      await api.patch(`/projects/${selectedProject.id}`, {
        name: projectSettingsForm.name,
        departmentId: projectSettingsForm.departmentId ? Number(projectSettingsForm.departmentId) : undefined,
        managerIds: projectSettingsForm.managerIds.map(Number),
        endDate: projectSettingsForm.endDate,
        totalRewardPool: projectSettingsForm.totalRewardPool !== '' ? Number(projectSettingsForm.totalRewardPool) : 0,
        status: projectSettingsForm.status,
      });
      await refreshSelectedProject();
      await loadData();
      setMessage({ type: 'success', text: 'Project settings updated.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update project settings.' });
    }
  };

  const handleAddContributor = async () => {
    if (!addContributorId || !selectedProject) return;
    try {
      await api.post(`/projects/${selectedProject.id}/contributors/${addContributorId}`);
      setMessage({ type: 'success', text: 'Contributor added.' });
      setAddContributorId('');
      setShowAddContributor(false);
      await refreshSelectedProject();
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to add contributor.' });
    }
  };

  const handleRemoveContributor = async (userId) => {
    if (!selectedProject) return;
    try {
      await api.delete(`/projects/${selectedProject.id}/contributors/${userId}`);
      setMessage({ type: 'success', text: 'Contributor removed.' });
      await refreshSelectedProject();
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to remove contributor.' });
    }
  };

  const handleChangeQuotaFromRow = (quotaRow) => {
    if (!quotaRow) return;
    const contributor = quotaRow.user || users.find((u) => u.id === quotaRow.userId) || { id: quotaRow.userId };
    setSelectedContributor(contributor);
    setQuotaForm({
      month: Number(quotaRow.month),
      year: Number(quotaRow.year),
      quota: String(quotaRow.quota ?? ''),
    });
  };

  const handleTaskFieldChange = (task, field, value) => {
    setTaskEdits((prev) => ({
      ...prev,
      [task.id]: {
        priority: prev[task.id]?.priority ?? task.priority ?? 'medium',
        status: prev[task.id]?.status ?? task.status ?? 'todo',
        userId: prev[task.id]?.userId ?? Number(task.userId),
        [field]: field === 'userId' ? Number(value) : value,
      },
    }));
  };

  const handleUpdateTaskInline = async (task) => {
    const edit = taskEdits[task.id];
    if (!edit) return;

    try {
      await api.patch(`/tasks/${task.id}`, {
        priority: edit.priority,
        status: edit.status,
        userId: Number(edit.userId),
      });

      setTaskEdits((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });

      await refreshSelectedProject();
      setMessage({ type: 'success', text: 'Task updated successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update task.' });
    }
  };

  const filteredProjects = projects.filter((p) => {
    if (!departmentFilter) return true;
    return (p.departmentId || p.department?.id)?.toString() === departmentFilter;
  });

  if (loading) return <div className="page-loading">Loading projects...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><Briefcase size={24} /> Projects Management</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={loadData}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="tr-stats-grid">
        <div className="tr-stat-card">
          <div className="tr-stat-label">Total Projects</div>
          <div className="tr-stat-value">{filteredProjects.length}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Active Projects</div>
          <div className="tr-stat-value">{filteredProjects.filter((p) => p.status === 'active').length}</div>
        </div>
        <div className="tr-stat-card">
          <div className="tr-stat-label">Total Reward Pool</div>
          <div className="tr-stat-value">{filteredProjects.reduce((sum, p) => sum + Number(p.totalRewardPool || 0), 0).toFixed(2)}</div>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {/* Filter Section */}
      <div className="users-filters" style={{ marginBottom: 16 }}>
        <div className="filter-controls" style={{ gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Filter by Department:</label>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="filter-select"
            style={{ minWidth: 200 }}
          >
            <option value="">All Departments ({departments.length})</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="users-table-container tr-card" style={{ marginBottom: 16, padding: 16 }}>
        <h3 className="tr-card-header">{showForm ? 'Create New Project' : 'Projects List'}</h3>

        {!showForm ? (
          <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ marginBottom: 16 }}>
            <Plus size={14} /> New Project
          </button>
        ) : (
          <form onSubmit={handleCreateProject} className="tr-form-grid" style={{ gap: 10, marginBottom: 16 }}>
            <input
              className="tr-input"
              type="text"
              placeholder="Project Name *"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
            <select
              className="tr-select"
              value={form.departmentId}
              onChange={(e) => setForm((prev) => ({ ...prev, departmentId: e.target.value }))}
              required
            >
              <option value="">Select Department *</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <textarea
              className="tr-input"
              placeholder="Description (Optional)"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              style={{ gridColumn: '1 / -1' }}
            />
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>Start Date *</label>
              <input
                className="tr-input"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>End Date *</label>
              <input
                className="tr-input"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                style={{ width: '100%' }}
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Assign Project Managers (One or More) *
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 110, overflowY: 'auto', padding: 8, background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                {users.map((u) => {
                  const isChecked = form.managerIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 16,
                        fontSize: 12,
                        cursor: 'pointer',
                        background: isChecked ? 'rgba(99, 102, 241, 0.2)' : 'var(--bg-secondary)',
                        color: isChecked ? '#818cf8' : 'var(--text-primary)',
                        border: isChecked ? '1px solid #6366f1' : '1px solid var(--border-color)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((prev) => ({ ...prev, managerIds: [...prev.managerIds, u.id] }));
                          } else {
                            setForm((prev) => ({ ...prev, managerIds: prev.managerIds.filter((id) => id !== u.id) }));
                          }
                        }}
                      />
                      <span>{u.firstName} {u.lastName} <small style={{ opacity: 0.75 }}>({u.role})</small></span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Assign Initial Users / Contributors (Employees, Managers, Interns)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 120, overflowY: 'auto', padding: 8, background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                {users.map((u) => {
                  const isChecked = form.contributorIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 16,
                        fontSize: 12,
                        cursor: 'pointer',
                        background: isChecked ? 'rgba(34, 197, 94, 0.2)' : 'var(--bg-secondary)',
                        color: isChecked ? '#4ade80' : 'var(--text-primary)',
                        border: isChecked ? '1px solid #22c55e' : '1px solid var(--border-color)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((prev) => ({ ...prev, contributorIds: [...prev.contributorIds, u.id] }));
                          } else {
                            setForm((prev) => ({ ...prev, contributorIds: prev.contributorIds.filter((id) => id !== u.id) }));
                          }
                        }}
                      />
                      <span>{u.firstName} {u.lastName} <small style={{ opacity: 0.75 }}>({u.role} - {u.contractType || 'CDI'})</small></span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>Total Reward Pool (Optional)</label>
              <input
                className="tr-input"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={form.totalRewardPool}
                onChange={(e) => setForm((prev) => ({ ...prev, totalRewardPool: e.target.value }))}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, marginTop: 10 }}>
              <button className="btn btn-primary" type="submit"><Plus size={14} /> Create Project</button>
              <button className="btn btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {!showForm && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {filteredProjects.map((p) => (
              (() => {
                const statusStyle = getProjectStatusStyles(p.status);
                const dept = departments.find((d) => d.id === p.departmentId || d.id === p.department?.id) || p.department;
                const managersList = (p.managers && p.managers.length > 0)
                  ? p.managers.map((m) => `${m.firstName} ${m.lastName}`).join(', ')
                  : (p.manager ? `${p.manager.firstName} ${p.manager.lastName}` : 'Unassigned');
                return (
              <div
                key={p.id}
                className="tr-card"
                style={{
                  padding: 14,
                  cursor: 'pointer',
                  position: 'relative',
                  border: selectedProject?.id === p.id ? '2px solid #6d7cf5' : '1px solid var(--border-color)',
                  background: selectedProject?.id === p.id ? 'rgba(109,124,245,0.1)' : undefined,
                }}
                onClick={() => handleSelectProject(p.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 15 }}>{p.name}</h4>
                  <button
                    className="btn-icon delete"
                    title="Delete project"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjectToDelete(p);
                      setShowDeleteModal(true);
                    }}
                    style={{ padding: 4 }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                {dept && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 6 }}>
                    🏢 {dept.name}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  📅 {p.startDate} to {p.endDate}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  👤 Managers: <strong>{managersList}</strong>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  💰 Reward Pool: {p.totalRewardPool ? `${p.totalRewardPool} DT` : 'None (Optional)'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  👥 Contributors: {p.contributors?.length || 0}
                </div>
                <div style={{ marginTop: 10 }}>
                  <span
                    style={{
                      ...statusStyle,
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                    }}
                  >
                    {String(p.status || 'active').replace('_', ' ')}
                  </span>
                </div>
              </div>
                );
              })()
            ))}
          </div>
        )}
      </div>

      {selectedProject && (
        <div className="users-table-container tr-card" style={{ padding: 16 }}>
          {(() => {
            const selectedStatusStyle = getProjectStatusStyles(selectedProject.status);
            const dept = departments.find((d) => d.id === selectedProject.departmentId || d.id === selectedProject.department?.id) || selectedProject.department;
            const selectedManagers = (selectedProject.managers && selectedProject.managers.length > 0)
              ? selectedProject.managers.map((m) => `${m.firstName} ${m.lastName}`).join(', ')
              : (selectedProject.manager ? `${selectedProject.manager.firstName} ${selectedProject.manager.lastName}` : 'Unassigned');
            return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <h3 className="tr-card-header" style={{ margin: 0 }}>{selectedProject.name}</h3>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {dept && <span style={{ color: '#6366f1', fontWeight: 700, marginRight: 10 }}>🏢 {dept.name}</span>}
                👤 Managers: <strong style={{ marginRight: 10 }}>{selectedManagers}</strong>
                📅 {selectedProject.startDate} → {selectedProject.endDate} &nbsp;|&nbsp; 💰 Pool: <strong>{selectedProject.totalRewardPool || 0} DT</strong>
              </div>
              <div style={{ marginTop: 8 }}>
                <span
                  style={{
                    ...selectedStatusStyle,
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                  }}
                >
                  {String(selectedProject.status || 'active').replace('_', ' ')}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => {
                  setProjectToDelete(selectedProject);
                  setShowDeleteModal(true);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12 }}
              >
                <Trash2 size={14} /> Delete Project
              </button>
              <button
                onClick={() => setSelectedProject(null)}
                style={{
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white'
                }}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
            );
          })()}

          <div className="tr-card" style={{ padding: 12, border: '1px solid var(--border-color)', marginBottom: 14 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Edit Project Details & Settings</h4>
            <form onSubmit={handleUpdateProjectSettings} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Project Name</label>
                <input
                  className="tr-input"
                  type="text"
                  value={projectSettingsForm.name}
                  onChange={(e) => setProjectSettingsForm((prev) => ({ ...prev, name: e.target.value }))}
                  style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Department *</label>
                <select
                  className="tr-select"
                  value={projectSettingsForm.departmentId}
                  onChange={(e) => setProjectSettingsForm((prev) => ({ ...prev, departmentId: e.target.value }))}
                  style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Project Managers (One or More)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 80, overflowY: 'auto', padding: 6, background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                  {users.map((u) => {
                    const isChecked = projectSettingsForm.managerIds.includes(u.id);
                    return (
                      <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', fontSize: 11, borderRadius: 12, cursor: 'pointer', background: isChecked ? 'rgba(99,102,241,0.2)' : 'var(--bg-secondary)', color: isChecked ? '#818cf8' : 'var(--text-primary)' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setProjectSettingsForm((prev) => ({ ...prev, managerIds: [...prev.managerIds, u.id] }));
                            } else {
                              setProjectSettingsForm((prev) => ({ ...prev, managerIds: prev.managerIds.filter((id) => id !== u.id) }));
                            }
                          }}
                        />
                        {u.firstName} {u.lastName} ({u.role})
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>End Date</label>
                <input
                  className="tr-input"
                  type="date"
                  value={projectSettingsForm.endDate}
                  onChange={(e) => setProjectSettingsForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Reward Pool (Optional)</label>
                <input
                  className="tr-input"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={projectSettingsForm.totalRewardPool}
                  onChange={(e) => setProjectSettingsForm((prev) => ({ ...prev, totalRewardPool: e.target.value }))}
                  style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Status</label>
                <select
                  className="tr-select"
                  value={projectSettingsForm.status}
                  onChange={(e) => setProjectSettingsForm((prev) => ({ ...prev, status: e.target.value }))}
                  style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <button className="btn btn-primary" type="submit" style={{ padding: '7px 12px', fontSize: 12, width: '100%' }}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* ── Left: Contributors ── */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h4 style={{ margin: 0 }}>Contributors ({selectedProject.contributors?.length || 0})</h4>
                <button
                  onClick={() => setShowAddContributor((v) => !v)}
                  style={{
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#22c55e', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white'
                  }}
                  title="Add contributor"
                >
                  <UserPlus size={14} />
                </button>
              </div>

              {showAddContributor && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <select
                    className="tr-select"
                    value={addContributorId}
                    onChange={(e) => setAddContributorId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select user...</option>
                    {users
                      .filter((u) => !(selectedProject.contributors || []).some((c) => c.id === u.id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName} ({u.role}{u.contractType ? ` - ${u.contractType}` : ''})
                        </option>
                      ))}
                  </select>
                  <button className="btn btn-primary" onClick={handleAddContributor} disabled={!addContributorId}>
                    <Plus size={13} /> Confirm
                  </button>
                </div>
              )}

              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedProject.contributors || []).length === 0 ? (
                    <tr><td colSpan="3" className="empty-row">No contributors yet. Click "Add" to add contributors.</td></tr>
                  ) : (selectedProject.contributors || []).map((c) => {
                    const user = users.find((u) => u.id === c.id) || c;
                    const isSelected = selectedContributor?.id === c.id;
                    return (
                      <tr
                        key={c.id}
                        onClick={() => setSelectedContributor(isSelected ? null : c)}
                        style={{ cursor: 'pointer', background: isSelected ? 'rgba(109,124,245,0.15)' : undefined }}
                      >
                        <td>{user.firstName} {user.lastName}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{user.email || '—'}</td>
                        <td>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '2px 8px', fontSize: 11 }}
                            onClick={(e) => { e.stopPropagation(); handleRemoveContributor(c.id); }}
                          >
                            <X size={11} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Right: Rewards ── */}
            <div>
              <>
                  <h4 style={{ margin: '0 0 12px' }}>Rewards</h4>
                  {selectedProject.rewards && selectedProject.rewards.length > 0 ? (
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Contributor</th>
                          <th>Score</th>
                          <th>×</th>
                          <th>Reward</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedProject.rewards.map((r) => {
                          const user = users.find((u) => u.id === r.userId);
                          return (
                            <tr key={r.id}>
                              <td>{user ? `${user.firstName} ${user.lastName}` : `#${r.userId}`}</td>
                              <td>{r.totalScore}</td>
                              <td>{r.attendanceMultiplier}</td>
                              <td>{r.allocatedReward}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                      No rewards data available.
                    </div>
                  )}
                </>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 16 }}>
            <div>
              <h4 style={{ margin: '0 0 12px' }}>All Quotas</h4>
              {(selectedProject.quotas || []).length > 0 ? (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Contributor</th>
                      <th>Month</th>
                      <th>Year</th>
                      <th>Quota</th>
                      <th>Completion</th>
                      <th style={{ width: 80 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedProject.quotas || []).map((q) => {
                      const user = q.user || users.find((u) => u.id === q.userId);
                      const weightedCompletion = getQuotaWeightedCompletion(q);
                      return (
                        <tr key={q.id}>
                          <td>{user ? `${user.firstName} ${user.lastName}` : `#${q.userId}`}</td>
                          <td>{q.month}</td>
                          <td>{q.year}</td>
                          <td>{q.quota}</td>
                          <td>{`Quota: ${weightedCompletion}/${Number(q.quota || 0)}`}</td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: 11, width: '100%' }}
                              onClick={() => handleChangeQuotaFromRow(q)}
                            >
                              Change
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  No quotas set yet.
                </div>
              )}
            </div>

            <div>
              <h4 style={{ margin: '0 0 12px' }}>All Tasks</h4>
              {(selectedProject.tasks || []).length > 0 ? (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Assigned To</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Due</th>
                      <th style={{ width: 90 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedProject.tasks || []).map((t) => {
                      const edit = taskEdits[t.id] || {
                        priority: t.priority || 'medium',
                        status: t.status || 'todo',
                        userId: Number(t.userId),
                      };
                      return (
                        <tr key={t.id}>
                          <td>{t.title}</td>
                          <td>
                            <select
                              className="tr-select"
                              value={edit.userId}
                              onChange={(e) => handleTaskFieldChange(t, 'userId', e.target.value)}
                              style={{ fontSize: 12, padding: '5px 6px', minWidth: 130 }}
                            >
                              {(selectedProject.contributors || []).map((c) => {
                                const cu = users.find((u) => u.id === c.id) || c;
                                return (
                                  <option key={c.id} value={c.id}>
                                    {cu.firstName} {cu.lastName}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td>
                            <select
                              className="tr-select"
                              value={edit.priority}
                              onChange={(e) => handleTaskFieldChange(t, 'priority', e.target.value)}
                              style={{ fontSize: 12, padding: '5px 6px', textTransform: 'capitalize' }}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className="tr-select"
                              value={edit.status}
                              onChange={(e) => handleTaskFieldChange(t, 'status', e.target.value)}
                              style={{ fontSize: 12, padding: '5px 6px' }}
                            >
                              <option value="todo">Todo</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td>{t.dueDate || '—'}</td>
                          <td>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '4px 8px', fontSize: 11, width: '100%' }}
                              onClick={() => handleUpdateTaskInline(t)}
                            >
                              Save
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                  No tasks assigned to this project yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Contributor Profile Modal ── */}
      {selectedContributor && (() => {
        const user = users.find((u) => u.id === selectedContributor.id) || selectedContributor;
        const reward = (selectedProject?.rewards || []).find((r) => r.userId === selectedContributor.id);
        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, padding: 20
            }}
            onClick={() => setSelectedContributor(null)}
          >
            <div
              className="tr-card"
              style={{
                maxWidth: 720, width: '100%', padding: 16, position: 'relative',
                maxHeight: 'calc(100vh - 40px)', overflow: 'hidden',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedContributor(null)}
                style={{
                  position: 'absolute', top: 16, right: 16,
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'white'
                }}
                title="Close"
              >
                <X size={14} />
              </button>

              <h3 style={{ margin: '0 0 10px', fontSize: 18 }}>Contributor Profile</h3>

              {message && (
                <div className={`message ${message.type}`} style={{ marginBottom: 10, fontSize: 12 }}>
                  {message.text}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 18, fontWeight: 600
                }}>
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{user.firstName} {user.lastName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{user.email || 'No email'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Role: {user.role || 'N/A'}
                  </div>
                </div>
              </div>

              {reward && (
                <>
                  <div className="tr-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 12, gap: 8 }}>
                    <div className="tr-stat-card" style={{ padding: 8 }}>
                      <div className="tr-stat-label">Total Score</div>
                      <div className="tr-stat-value">{reward.totalScore}</div>
                    </div>
                    <div className="tr-stat-card" style={{ padding: 8 }}>
                      <div className="tr-stat-label">Multiplier</div>
                      <div className="tr-stat-value">×{reward.attendanceMultiplier}</div>
                    </div>
                    <div className="tr-stat-card" style={{ padding: 8 }}>
                      <div className="tr-stat-label">Allocated Reward</div>
                      <div className="tr-stat-value">{reward.allocatedReward}</div>
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                <div className="tr-card" style={{ padding: 10, border: '1px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Set Monthly Quota</h4>
                  <form onSubmit={handleSetQuota} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6, alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Month</label>
                      <input
                        className="tr-input"
                        type="number"
                        min="1"
                        max="12"
                        placeholder="1-12"
                        value={quotaForm.month}
                        onChange={(e) => setQuotaForm((prev) => ({ ...prev, month: Number(e.target.value) }))}
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Year</label>
                      <input
                        className="tr-input"
                        type="number"
                        placeholder="YYYY"
                        value={quotaForm.year}
                        onChange={(e) => setQuotaForm((prev) => ({ ...prev, year: Number(e.target.value) }))}
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Quota</label>
                      <input
                        className="tr-input"
                        type="number"
                        placeholder="Tasks"
                        value={quotaForm.quota}
                        onChange={(e) => setQuotaForm((prev) => ({ ...prev, quota: e.target.value }))}
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        required
                      />
                    </div>
                    <button className="btn btn-primary" type="submit" style={{ padding: '6px 10px', fontSize: 12, marginBottom: 1, width: '100%' }}>
                      Set
                    </button>
                  </form>
                </div>

                <div className="tr-card" style={{ padding: 10, border: '1px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Assign Task</h4>
                  <form onSubmit={handleAssignTask} style={{ display: 'grid', gap: 6 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Task Title</label>
                      <input
                        className="tr-input"
                        type="text"
                        placeholder="Task title"
                        value={assignTaskForm.title}
                        onChange={(e) => setAssignTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Description</label>
                      <input
                        className="tr-input"
                        type="text"
                        placeholder="Description (optional)"
                        value={assignTaskForm.description}
                        onChange={(e) => setAssignTaskForm((prev) => ({ ...prev, description: e.target.value }))}
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Priority</label>
                        <select
                          className="tr-select"
                          value={assignTaskForm.priority}
                          onChange={(e) => setAssignTaskForm((prev) => ({ ...prev, priority: e.target.value }))}
                          style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Estimated Hours</label>
                        <input
                          className="tr-input"
                          type="number"
                          step="0.5"
                          placeholder="Hours"
                          value={assignTaskForm.estimatedHours}
                          onChange={(e) => setAssignTaskForm((prev) => ({ ...prev, estimatedHours: e.target.value }))}
                          style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: 11, color: 'var(--text-secondary)' }}>Due Date</label>
                        <input
                          className="tr-input"
                          type="date"
                          value={assignTaskForm.dueDate}
                          onChange={(e) => setAssignTaskForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                          style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'end' }}>
                        <button className="btn btn-primary" type="submit" style={{ padding: '6px 10px', fontSize: 12, width: '100%' }}>
                          Assign
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Project"
        message={`Are you sure you want to delete the project "${projectToDelete?.name}"? All associated quotas, tasks, and reward links for this project will be deleted.`}
        onConfirm={handleDeleteProject}
        onCancel={() => {
          setShowDeleteModal(false);
          setProjectToDelete(null);
        }}
        confirmText="Delete Project"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Projects;
